import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Archive, ArchiveRestore, Download, FileSpreadsheet, FileX2, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import { hoyISOBA } from "@/components/ClockBA";
import { Badge, Card, MesNav, Modal, PageHeader, btnPrim, btnSec, inputCls } from "@/components/rrhh/ui";

export default function CierresPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [mes, setMes] = useState(hoyISOBA().slice(0, 7));
  const [confirmar, setConfirmar] = useState(false);
  const [reabrir, setReabrir] = useState(null); // mes
  const [motivoReabrir, setMotivoReabrir] = useState("");

  const { data } = useQuery({
    queryKey: ["liq-cierres", mes],
    queryFn: async () => (await api.get("/liquidacion/cierres", { params: { mes } })).data,
    refetchInterval: 8000,
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["liq-cierres"] });
    qc.invalidateQueries({ queryKey: ["liq-resumen"] });
    qc.invalidateQueries({ queryKey: ["historial"] });
  };

  const mutCerrar = useMutation({
    mutationFn: async () => api.post("/liquidacion/cierres/cerrar", { mes }),
    onSuccess: () => {
      setConfirmar(false);
      invalidar();
      toast.success("Mes cerrado — el Excel ya se puede descargar definitivo");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const mutReabrir = useMutation({
    mutationFn: async ({ mes: m, motivo }) => api.post("/liquidacion/cierres/reabrir", { mes: m, motivo }),
    onSuccess: () => {
      setReabrir(null);
      setMotivoReabrir("");
      invalidar();
      toast.success("Mes reabierto — quedó registrado en el historial");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const actual = data?.actual;
  const cerrado = actual?.estado === "cerrado";
  const pend = data?.pendientes;
  const hayPendientes = (pend?.justificativos.length || 0) + (pend?.incompletas.length || 0) > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-cierres">
      <PageHeader
        titulo="Cierre de mes"
        subtitulo="Al cerrar, se genera el Excel con todo lo del mes. Un mes cerrado se puede reabrir, y queda registrado."
        testid="header-cierres"
      >
        <MesNav mes={mes} onChange={setMes} testid="nav-mes-cierres" />
      </PageHeader>

      <Card
        testid="card-cierre-actual"
        titulo={data?.mes_label ?? ""}
        subtitulo="Estado del período y lo que falta resolver antes de cerrar"
        right={
          <Badge color={cerrado ? "verde" : "gris"} testid="cierre-estado">
            {cerrado ? `Cerrado el ${fmtFecha(actual.cerrado_en)} por ${actual.cerrado_por_usuario}` : "Abierto"}
          </Badge>
        }
        className="mb-5"
      >
        {pend && (hayPendientes ? (
          <div className="mb-4 rounded-xl border border-amber-400/25 bg-amber-400/10 p-4" data-testid="cierre-pendientes">
            <p className="mb-2 text-sm font-semibold text-amber-300">Pendientes del mes</p>
            <ul className="space-y-1.5 text-sm text-gray-300">
              {pend.justificativos.map((j) => (
                <li key={j.id}>
                  · Justificativo sin resolver — <button className="font-semibold text-[#2DD4BF] hover:underline" onClick={() => navigate("/liquidacion/justificativos")}>{j.empleado_nombre}</button>
                  <span className="text-gray-500"> ({j.tipo_label} del {fmtFecha(j.fecha)})</span>
                </li>
              ))}
              {pend.incompletas.map((j) => (
                <li key={j.id}>
                  · Jornada incompleta sin ajustar — <button className="font-semibold text-[#2DD4BF] hover:underline" onClick={() => navigate(`/liquidacion/persona/${j.empleado_id}?mes=${mes}`)}>{j.empleado_nombre}</button>
                  <span className="text-gray-500"> ({fmtFecha(j.fecha)}, le falta la salida)</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mb-4 rounded-xl border border-[#2DD4BF]/20 bg-[#2DD4BF]/10 px-4 py-3 text-sm text-[#2DD4BF]" data-testid="cierre-sin-pendientes">
            No queda nada pendiente: ni justificativos sin resolver ni jornadas incompletas.
          </p>
        ))}

        <div className="flex flex-wrap gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/liquidacion/excel?mes=${mes}`}
            className={`${btnSec} flex items-center gap-2`}
            data-testid="btn-vista-previa-excel"
          >
            <FileSpreadsheet size={15} /> Vista previa del Excel
          </a>
          {!cerrado && (
            <button className={`${btnPrim} flex items-center gap-2`} onClick={() => setConfirmar(true)} data-testid="btn-cerrar-mes">
              <Archive size={15} /> Cerrar mes
            </button>
          )}
          {cerrado && (
            <>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/liquidacion/excel?mes=${mes}`}
                className={`${btnPrim} flex items-center gap-2`}
                data-testid="btn-descargar-excel"
              >
                <Download size={15} /> Descargar el Excel
              </a>
              <button className={`${btnSec} flex items-center gap-2`} onClick={() => setReabrir(mes)} data-testid="btn-reabrir-mes">
                <LockOpen size={14} /> Reabrir
              </button>
            </>
          )}
        </div>

        {cerrado && actual?.reaperturas?.length > 0 && (
          <p className="mt-3 text-xs text-gray-500">
            Se reabrió {actual.reaperturas.length} {actual.reaperturas.length === 1 ? "vez" : "veces"} antes de este cierre.
          </p>
        )}
      </Card>

      <Card testid="card-cierres-anteriores" titulo="Cierres anteriores" subtitulo="Cada cierre queda guardado con quién y cuándo. Se puede volver a descargar o reabrir." bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Período</th>
                <th className="px-4 py-3 font-semibold">Cerrado por</th>
                <th className="px-4 py-3 font-semibold">Fecha y hora</th>
                <th className="px-4 py-3 font-semibold">Reaperturas</th>
                <th className="px-5 py-3 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(data?.historial || []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center">
                    <Archive size={26} className="mx-auto text-gray-600" />
                    <p className="mt-3 text-sm font-semibold text-gray-300">Todavía no hay meses cerrados</p>
                    <p className="text-xs text-gray-500">Cuando cierres el primero, aparece acá con su Excel.</p>
                  </td>
                </tr>
              )}
              {data?.historial.map((c) => (
                <tr key={c.mes} className="border-b border-white/5 last:border-0" data-testid={`cierre-${c.mes}`}>
                  <td className="px-5 py-3 font-mono-num text-sm font-semibold text-white">{c.mes}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-gray-300">{c.cerrado_por_usuario}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono-num text-xs text-gray-400">
                    {fmtFecha(c.cerrado_en)} {c.cerrado_en?.slice(11, 16)}
                  </td>
                  <td className="px-4 py-3 font-mono-num text-xs text-gray-400">{c.reaperturas?.length || 0}</td>
                  <td className="px-5 py-3">
                    <span className="flex gap-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/liquidacion/excel?mes=${c.mes}`} className={`${btnSec} !px-2.5 !py-1 text-xs`} data-testid={`descargar-${c.mes}`}>
                        <Download size={12} className="mr-1 inline" /> Descargar Excel
                      </a>
                      <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => setReabrir(c.mes)} data-testid={`reabrir-${c.mes}`}>
                        <ArchiveRestore size={12} className="mr-1 inline" /> Reabrir
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {confirmar && (
        <Modal titulo={`Cerrar ${data?.mes_label}`} onClose={() => setConfirmar(false)} testid="modal-cerrar-mes">
          {hayPendientes ? (
            <>
              <p className="mb-2 text-sm font-semibold text-amber-300">Quedan pendientes que no se van a ajustar:</p>
              <ul className="mb-4 space-y-1.5 rounded-xl border border-amber-400/25 bg-amber-400/10 p-4 text-sm text-gray-300" data-testid="cierre-pendientes-modal">
                {pend.justificativos.map((j) => (
                  <li key={j.id}>· Justificativo sin resolver: {j.empleado_nombre} ({j.tipo_label} del {fmtFecha(j.fecha)})</li>
                ))}
                {pend.incompletas.map((j) => (
                  <li key={j.id}>· Jornada incompleta sin ajustar: {j.empleado_nombre} ({fmtFecha(j.fecha)})</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mb-4 text-sm text-gray-400">No hay pendientes. Al cerrar, el mes queda congelado y el Excel pasa a ser el definitivo.</p>
          )}
          <p className="mb-4 text-sm text-gray-400">Si hace falta tocar algo después, el mes se puede reabrir con motivo.</p>
          <div className="flex justify-end gap-2">
            <button className={btnSec} onClick={() => setConfirmar(false)}>Cancelar</button>
            <button className={btnPrim} disabled={mutCerrar.isPending} onClick={() => mutCerrar.mutate()} data-testid="confirmar-cerrar-mes">
              {hayPendientes ? "Cerrar igual" : "Cerrar el mes"}
            </button>
          </div>
        </Modal>
      )}

      {reabrir && (
        <Modal titulo={`Reabrir ${reabrir}`} onClose={() => { setReabrir(null); setMotivoReabrir(""); }} testid="modal-reabrir">
          <p className="mb-4 flex items-start gap-2 text-sm text-gray-400">
            <FileX2 size={16} className="mt-0.5 shrink-0 text-amber-300" />
            Vuelve a estar editable para ajustes. La reapertura queda registrada con tu usuario y el motivo.
          </p>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Motivo (obligatorio)</label>
          <textarea
            value={motivoReabrir}
            onChange={(e) => setMotivoReabrir(e.target.value)}
            rows={2}
            placeholder="ej. llegó un comprobante tarde"
            className={`${inputCls} w-full resize-none`}
            data-testid="reabrir-motivo"
          />
          <div className="mt-5 flex justify-end gap-2">
            <button className={btnSec} onClick={() => { setReabrir(null); setMotivoReabrir(""); }}>Cancelar</button>
            <button
              className={btnPrim}
              disabled={mutReabrir.isPending || motivoReabrir.trim().length < 3}
              onClick={() => mutReabrir.mutate({ mes: reabrir, motivo: motivoReabrir.trim() })}
              data-testid="confirmar-reabrir"
            >
              Reabrir el mes
            </button>
          </div>
        </Modal>
      )}
    </motion.div>
  );
}
